import React, { useState, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as XIcon } from '../../assets/xIcon.svg';

interface MenuUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => void;
}

const MenuUploadModal: React.FC<MenuUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
}) => {
  const { t } = useTranslation(['catalogue']);
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setFiles(filesArray);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files) {
      const filesArray = Array.from(e.dataTransfer.files);
      setFiles(filesArray);
    }
  };

  const handleUpload = () => {
    if (files.length > 0) {
      onUpload(files);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as='div' className='relative z-50' onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black bg-opacity-25' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4 text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all'>
                <div className='flex justify-between items-center mb-4'>
                  <Dialog.Title
                    as='h3'
                    className='text-lg font-medium leading-6 text-gray-900'
                  >
                    {t('upload_menu.title', 'Upload Your Menu')}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className='rounded-full p-1 hover:bg-gray-200 transition duration-200'
                  >
                    <XIcon className='h-5 w-5 text-gray-500' />
                  </button>
                </div>

                <div className='mt-2'>
                  <p className='text-sm text-gray-500'>
                    {t(
                      'upload_menu.description',
                      'Upload your menu as a PDF, image, or document file. Our AI will automatically create your catalogue items.'
                    )}
                  </p>
                </div>

                <div
                  className={`mt-6 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ease-in-out hover:border-droov hover:bg-droov/5 hover:bg-gray-50 ${
                    isDragging
                      ? 'border-droov bg-droov/5 bg-gray-50'
                      : 'border-gray-300'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={triggerFileInput}
                >
                  <input
                    type='file'
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept='.pdf,.doc,.docx,.png,.jpg,.jpeg'
                    className='hidden'
                    multiple
                  />

                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-10 w-10 mx-auto text-droov mb-2'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12'
                    />
                  </svg>

                  <p className='text-sm font-medium text-gray-700'>
                    {t(
                      'upload_menu.drag_drop',
                      'Drag & drop files here or click to browse'
                    )}
                  </p>
                  <p className='text-xs text-gray-500 mt-1'>
                    {t(
                      'upload_menu.supported_formats',
                      'Supported formats: PDF, DOC, DOCX, PNG, JPG, JPEG'
                    )}
                  </p>
                </div>

                {files.length > 0 && (
                  <div className='mt-4'>
                    <p className='text-sm font-medium text-gray-700 mb-2'>
                      {t('upload_menu.selected_files', 'Selected Files:')}
                    </p>
                    <ul className='space-y-2'>
                      {files.map((file, index) => (
                        <li
                          key={index}
                          className='flex justify-between items-center bg-gray-50 p-2 rounded'
                        >
                          <span className='text-sm text-gray-700 truncate max-w-xs'>
                            {file.name}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                            className='text-red-500 hover:text-red-700'
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              className='h-5 w-5'
                              viewBox='0 0 20 20'
                              fill='currentColor'
                            >
                              <path
                                fillRule='evenodd'
                                d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                                clipRule='evenodd'
                              />
                            </svg>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className='mt-6 flex justify-end space-x-3'>
                  <button
                    type='button'
                    className='inline-flex justify-center cancel_button px-4 py-2 font-medium'
                    onClick={onClose}
                  >
                    {t('cancel.button', 'Cancel')}
                  </button>
                  <button
                    type='button'
                    className={`inline-flex justify-center relative_blue_button px-4 py-2 font-medium ${
                      files.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    onClick={handleUpload}
                    disabled={files.length === 0}
                  >
                    {t('upload.button', 'Upload')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MenuUploadModal;
